import { For, Show, Switch, Match, createSignal, createMemo, createResource, onMount } from 'solid-js';
import { route, navigate } from './lib/router.js';
import { theme, toggleTheme } from './lib/theme.js';
import { APP_VERSION } from './lib/version.js';
import { me, companies, activeCompany, setActiveCompany, checkSession, logout, karyawanMe, checkKaryawanSession } from './lib/session.js';
import Absensi from './pages/Absensi.jsx';
import Bantuan from './pages/Bantuan.jsx';
import DaftarLembur from './pages/DaftarLembur.jsx';
import KaryawanPortal from './pages/KaryawanPortal.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Karyawan from './pages/Karyawan.jsx';
import Kontrak from './pages/Kontrak.jsx';
import Laporan from './pages/Laporan.jsx';
import KomponenGaji from './pages/KomponenGaji.jsx';
import Login from './pages/Login.jsx';
import Organisasi from './pages/Organisasi.jsx';
import Pengaturan from './pages/Pengaturan.jsx';
import PersetujuanIzin from './pages/PersetujuanIzin.jsx';
import Placeholder from './pages/Placeholder.jsx';
import ProsesGaji from './pages/ProsesGaji.jsx';
import Slip from './pages/Slip.jsx';
import VariabelGaji from './pages/VariabelGaji.jsx';

// Navigation map — one place to add a page. `el` is a lazy component ref so
// pages stay plain functions; no framework routing layer.
const NAV = [
  { section: 'Utama' },
  { path: '/dashboard', label: 'Dashboard', ico: '◫', el: Dashboard },
  { section: 'Master' },
  { path: '/karyawan',  label: 'Karyawan',        ico: '☷', el: Karyawan },
  { path: '/kontrak',   label: 'Kontrak',         ico: '⏳', el: Kontrak },
  { path: '/organisasi', label: 'Organisasi',     ico: '⌗', el: Organisasi },
  { path: '/variabel',  label: 'Variabel Gaji',   ico: '𝑥', el: VariabelGaji },
  { path: '/komponen',  label: 'Komponen Gaji',   ico: '∑', el: KomponenGaji },
  { section: 'Proses' },
  { path: '/absensi',   label: 'Absensi',         ico: '⏱', el: Absensi },
  { path: '/izin',      label: 'Persetujuan Izin', ico: '☑', el: PersetujuanIzin },
  { path: '/lembur',    label: 'Daftar Lembur',   ico: '▦', el: DaftarLembur },
  { path: '/gaji',      label: 'Pemrosesan Gaji',  ico: '⚙', el: ProsesGaji },
  { path: '/slip',      label: 'Slip Gaji',       ico: '▤', el: Slip },
  { section: 'Lainnya' },
  { path: '/laporan',   label: 'Laporan',         ico: '◔', el: Laporan },
  { path: '/pengaturan', label: 'Pengaturan',     ico: '⚒', el: Pengaturan },
  { path: '/bantuan',   label: 'Bantuan',         ico: '?', el: Bantuan },
];

const PAGES = Object.fromEntries(NAV.filter(n => n.path).map(n => [n.path, n]));

export default function App() {
  const page = createMemo(() => PAGES[route()] ?? PAGES['/dashboard']);
  // Active company drives the top-left brand logo (falls back to the MP mark).
  const activeCo = createMemo(() => companies().find((c) => c.id === activeCompany()));
  // Mobile nav drawer: the sidebar is off-canvas below 820px (see global.css).
  const [navOpen, setNavOpen] = createSignal(false);
  // Desktop: the SAME hamburger collapses the sidebar to an icon rail (persisted).
  const [collapsed, setCollapsed] = createSignal((() => {
    try { return localStorage.getItem('sidebar_collapsed') === '1'; } catch (_) { return false; }
  })());
  // One hamburger, two behaviours by width: ≤820px → slide the drawer; wider →
  // collapse to the icon rail.
  function toggleNav() {
    if (window.matchMedia('(max-width: 820px)').matches) { setNavOpen((o) => !o); return; }
    const v = !collapsed();
    setCollapsed(v);
    try { localStorage.setItem('sidebar_collapsed', v ? '1' : '0'); } catch (_) {}
  }
  onMount(() => { checkSession(); checkKaryawanSession(); });

  return (
    <Switch>
      {/* both sessions resolve before we decide which portal to show */}
      <Match when={me() === undefined || karyawanMe() === undefined}>
        <div class="login-wrap"><div class="kg-meta-text">memeriksa sesi…</div></div>
      </Match>
      <Match when={karyawanMe() && !me()}>
        <KaryawanPortal />
      </Match>
      <Match when={me() === null}>
        <Login />
      </Match>
      {/* authenticated but no company assigned — explain instead of a broken shell */}
      <Match when={me() && companies().length === 0}>
        <div class="login-wrap">
          <div class="card card-pad" style="max-width:440px;text-align:center">
            <div style="font-size:34px;margin-bottom:6px">🔒</div>
            <h3 style="margin:0 0 8px">Belum ada perusahaan</h3>
            <p class="kg-meta-text" style="margin:0 0 16px">
              Akun <b>{me().displayName || me().username}</b> belum ditugaskan ke
              perusahaan mana pun. Minta superadmin menugaskan minimal satu
              perusahaan di Pengaturan → Pengguna, lalu masuk kembali.
            </p>
            <button class="btn ghost" onClick={logout}>Keluar</button>
          </div>
        </div>
      </Match>
      <Match when={me()}>
        <div class="shell" classList={{ collapsed: collapsed() }}>
          <aside class="sidebar" classList={{ open: navOpen() }}>
            <div class="brand">
              <Show when={activeCo()?.hasLogo} fallback={<div class="brand-mark">MP</div>}>
                <img class="brand-logo" src={`/api/company/${activeCompany()}/logo`} alt={activeCo()?.name || 'logo'} />
              </Show>
              <div class="brand-name">
                Metalindo Payroll
                <small>Sistem Penggajian</small>
              </div>
            </div>
            <nav class="nav">
              <For each={NAV}>{(item) =>
                item.section
                  ? <div class="nav-section">{item.section}</div>
                  : <button
                      class="nav-item"
                      classList={{ active: page().path === item.path }}
                      title={item.label}
                      onClick={() => { navigate(item.path); setNavOpen(false); }}
                    >
                      <span class="ico">{item.ico}</span><span class="label">{item.label}</span>
                    </button>
              }</For>
            </nav>
            <div class="sidebar-foot">
              <button class="btn ghost nav-theme" onClick={toggleTheme} title="Ganti tema">
                <span class="ico">{theme() === 'dark' ? '☀' : '🌙'}</span>
                <span class="label"><Show when={theme() === 'dark'} fallback="Mode gelap">Mode terang</Show></span>
              </button>
              <div class="app-version">Payroll Metalindo · v{APP_VERSION}</div>
            </div>
          </aside>

          {/* Mobile: tap-to-close backdrop behind the open drawer. */}
          <Show when={navOpen()}>
            <div class="scrim" onClick={() => setNavOpen(false)} />
          </Show>

          <div class="main">
            <header class="topbar">
              <button class="btn ghost nav-toggle" onClick={toggleNav}
                aria-label="Menu" title="Menu (ciutkan / buka sidebar)">☰</button>
              <h1>{page().label}</h1>
              <div class="spacer" />
              {/* company switcher — pages re-mount on switch so every
                  resource refetches under the new X-Company-ID */}
              <Show when={companies().length > 1} fallback={
                <span class="topbar-company">{companies()[0]?.name}</span>
              }>
                <select
                  class="kg-select topbar-switch"
                  value={activeCompany()}
                  onInput={(e) => { setActiveCompany(Number(e.currentTarget.value)); window.location.reload(); }}
                >
                  <For each={companies()}>{(c) => <option value={c.id}>{c.name}</option>}</For>
                </select>
              </Show>
              <NotifBell />
              <span class="topbar-user">👤 {me().displayName || me().username}</span>
              <button class="btn ghost" onClick={logout}>Keluar</button>
            </header>
            <main class="content">
              {page().el({ title: page().label })}
            </main>
          </div>
        </div>
      </Match>
    </Switch>
  );
}

// NotifBell — topbar bell with upcoming birthdays + contract expirations for the
// active company. Refetches when opened; clicking an item jumps to Karyawan.
function NotifBell() {
  const [list, { refetch }] = createResource(async () => {
    const r = await fetch('/api/notifications');
    return r.ok ? r.json() : [];
  });
  const [open, setOpen] = createSignal(false);
  const count = () => list()?.length ?? 0;
  function toggle() {
    if (!open()) refetch();
    setOpen(!open());
  }
  const text = (n) => {
    const when = n.days === 0 ? 'hari ini' : `dalam ${n.days} hari`;
    return n.kind === 'birthday'
      ? `ulang tahun ${when} · ${n.date}`
      : `kontrak berakhir ${when} · ${n.date}`;
  };

  return (
    <div class="notif">
      <button class="btn ghost notif-bell" onClick={toggle} title="Notifikasi" aria-label="Notifikasi">
        🔔<Show when={count() > 0}><span class="notif-badge">{count() > 99 ? '99+' : count()}</span></Show>
      </button>
      <Show when={open()}>
        <div class="notif-scrim" onClick={() => setOpen(false)} />
        <div class="notif-pop">
          <div class="notif-head">Notifikasi <span class="kg-meta-text">({count()})</span></div>
          <Show when={count() === 0}>
            <div class="notif-empty">Tidak ada notifikasi.</div>
          </Show>
          <div class="notif-list">
            <For each={list() ?? []}>{(n) =>
              <a class="notif-item" href="#/karyawan" onClick={() => setOpen(false)}>
                <span class="notif-ico">{n.kind === 'birthday' ? '🎂' : '📄'}</span>
                <div class="notif-body">
                  <div class="notif-title">{n.name} <span class="kg-meta-text">{n.nik}</span></div>
                  <div class="notif-sub">{text(n)}</div>
                </div>
              </a>
            }</For>
          </div>
        </div>
      </Show>
    </div>
  );
}
